import React from 'react';
import cls from 'classnames';
import { Button } from 'react-bootstrap';
import DOMPurify from 'dompurify';
import { ReactComponent as PropdocsIcon } from '../../shared/icons/propdocs-icon.svg';
import Avatar from '../avatar';
import { TitleFormattedMessage } from '../titleFormattedMessage';
import styles from './ActivitiesCard.module.scss';
import { ActivityDateChat } from './ActivityDateChat';

export type ActivityPropTypes = {
  id: string;
  title: {
    id: string | number;
    defaultMessage: string;
  };
  time: string | null;
  conductor: string | undefined;
};

export enum DealActivityVerbs {
  SIGNER_SIGNED = 'document-signer-signed',
  EXECUTED = 'document-executed',
  FULL_APPROVAL = 'document-approval-request-approved',
  EMAIL_REPLY_RECEIVED = 'document-feedback-created',
  SIGNING_PREP_STARTED = 'document-signing-preparation-started',
  SIGNING_PREP_CANCELED = 'document-signing-preparation-canceled',
  DOCUMENT_REVIEW_REQUESTED = 'document-review-requested',
  PDF_EXPORTED = 'document-pdf-exported',
}

export type ActivityPropsType = {
  id: string;
  description?: string;
  time?: string;
  actor_data: {
    full_name: string;
    email: string;
  };
  object_data: {
    object_id: string;
  };
  verb: string;
};

const AVATAR_FONT_COLOR = '#737373';
const AVATAR_FONT_SIZE = '13px';
const AVATAR_FILL_COLOR = '#ffff';

export const RenderDealActivityData = (
  activityProps: ActivityPropsType,
  isSidebar: boolean,
  setSelectedActivity: (activity: ActivityPropsType | null, activityType?: string) => void
) => {
  const isDocumentApprovedActivity = activityProps.verb === DealActivityVerbs.FULL_APPROVAL;
  const isDocumentSignedBySignerActivity = activityProps.verb === DealActivityVerbs.SIGNER_SIGNED;
  const isDocumentExecutedActivity = activityProps.verb === DealActivityVerbs.EXECUTED;
  const isEmailReplyReceivedActivity = activityProps.verb === DealActivityVerbs.EMAIL_REPLY_RECEIVED;
  const isSigningPrepStartedActivity = activityProps.verb === DealActivityVerbs.SIGNING_PREP_STARTED;
  const isSigningPrepCanceledActivity = activityProps.verb === DealActivityVerbs.SIGNING_PREP_CANCELED;
  const isDocumentReviewRequestedActivity = activityProps.verb === DealActivityVerbs.DOCUMENT_REVIEW_REQUESTED;
  const isPdfExportedActivity = activityProps.verb === DealActivityVerbs.PDF_EXPORTED;

  return (
    <div
      key={activityProps.id}
      id={`button--${activityProps.id}`}
      className={cls(styles.activityCardItem, { [styles.activityCardSidebarItem]: isSidebar })}
    >
      <div className={styles.activityTitle}>
        {isDocumentApprovedActivity ||
        isDocumentSignedBySignerActivity ||
        isDocumentExecutedActivity ||
        isEmailReplyReceivedActivity ||
        isSigningPrepStartedActivity ||
        isSigningPrepCanceledActivity ||
        isDocumentReviewRequestedActivity ||
        isPdfExportedActivity ? (
          <span className={styles.propdocsAvatar}>
            <PropdocsIcon height={28} />
          </span>
        ) : (
          <span className={styles.userAvatar}>
            <Avatar
              email={activityProps.actor_data?.email}
              fullName={activityProps.actor_data?.full_name}
              title={activityProps.actor_data?.full_name}
              size={24}
              fontSize={AVATAR_FONT_SIZE}
              fontColor={AVATAR_FONT_COLOR}
              backgroundColor={AVATAR_FILL_COLOR}
            />
          </span>
        )}

        <span className={styles.activityActorName}>
          {isDocumentExecutedActivity || isDocumentApprovedActivity
            ? ''
            : isDocumentSignedBySignerActivity || isEmailReplyReceivedActivity
              ? activityProps.actor_data?.email
              : isSigningPrepStartedActivity || isSigningPrepCanceledActivity || isDocumentReviewRequestedActivity || isPdfExportedActivity
                ? activityProps.actor_data?.full_name
                : activityProps.actor_data?.full_name}
        </span>

        <span className={styles.activityDate}>
          <ActivityDateChat
            datetime={
              activityProps.time && (activityProps.time.endsWith('Z') || activityProps.time.includes('+'))
                ? activityProps.time
                : `${activityProps.time}Z`
            }
            intervalInSecond={60}
          />
        </span>
      </div>

      <div className={styles.activityDescription}>
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(activityProps.description || ''),
          }}
        />
      </div>

      {isEmailReplyReceivedActivity && (
        <Button
          variant="outline-primary"
          className={styles.viewEmailButton}
          onClick={() => {
            setSelectedActivity(activityProps, 'email-reply');
          }}
        >
          <TitleFormattedMessage id="cta.view-email-reply" defaultMessage={'View Email Reply'} />
        </Button>
      )}

      {isSigningPrepStartedActivity && (activityProps as any).additional_message && (
        <Button
          variant="outline-primary"
          className={styles.viewEmailButton}
          onClick={() => {
            setSelectedActivity(activityProps, 'signing-prep');
          }}
        >
          <TitleFormattedMessage id="cta.view-message" defaultMessage={'View Message'} />
        </Button>
      )}

      {isSigningPrepCanceledActivity && (activityProps as any).additional_message && (
        <Button
          variant="outline-primary"
          className={styles.viewEmailButton}
          onClick={() => {
            setSelectedActivity(activityProps, 'signing-prep-canceled');
          }}
        >
          <TitleFormattedMessage id="cta.view-message" defaultMessage={'View Message'} />
        </Button>
      )}

      {isDocumentReviewRequestedActivity && (activityProps as any).additional_message && (
        <Button
          variant="outline-primary"
          className={styles.viewEmailButton}
          onClick={() => {
            setSelectedActivity(activityProps, 'document-review-requested');
          }}
        >
          <TitleFormattedMessage id="cta.view-message" defaultMessage={'View Message'} />
        </Button>
      )}

      {isPdfExportedActivity && (
        <Button
          variant="outline-primary"
          className={styles.viewEmailButton}
          onClick={() => {
            setSelectedActivity(activityProps, 'pdf-download');
          }}
        >
          Download PDF
        </Button>
      )}
    </div>
  );
};
